/**
 * IncGstPriceInput — the ONE sell-price entry box.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * Every customer-facing sell price in this app is quoted, discussed and
 * advertised GST-INCLUSIVE (Australian Consumer Law requires the single
 * total price a consumer pays to be the prominent one). The office was still
 * typing ex-GST into every pricing box and mentally dividing by 1.1, which is
 * where "$91.75 stored, $100.93 advertised" mismatches came from.
 *
 * So: STAFF TYPE INC-GST. Storage stays EX-GST — Xero line items, Stripe
 * charges and the Utilmate hand-off all depend on the ex-tax basis, and the
 * repo-wide contract in src/utils/pricing.ts says the database is ex-GST.
 * This component is the conversion boundary, and the only place the
 * conversion is written, so the rounding behaves the same on all ~15 pricing
 * surfaces instead of being hand-rolled per modal.
 *
 * ── ROUNDING ───────────────────────────────────────────────────────────────
 * inc → ex rounds to cents, so a round-trip can move by a cent at very small
 * amounts. The helper line under the input therefore renders the RE-DERIVED
 * inc-GST figure (ex → inc), never the raw typed string: what the box says
 * the customer pays is what the customer will actually be shown.
 *
 * The tax word + rate come from the tenant (getTenantTaxRate) — nothing here
 * is AU-specific beyond the default rate.
 *
 * ── ENTRY MODE (additive, Proposal Builder V2) ─────────────────────────────
 * `entryMode` defaults to `'inc'` — everything above, unchanged for every
 * existing caller. A commercial proposal quoted ex-GST (`priceBasis: 'ex'`)
 * passes `entryMode="ex"`: the box is labelled "(ex GST)", the typed value IS
 * the ex-GST figure to store (`entryStringToExGst` → `roundMoney`), and the
 * shadow line still shows what the customer pays inc GST.
 *
 * ── CUSTOMER PAYS AS TYPED (additive, Proposal Builder V2 R2 §3.6) ─────────
 * `customerPaysAsTyped` (default off — every existing caller unchanged): in
 * `'inc'` entry the "Customer pays" figure is the typed amount to the cent
 * (what the office typed IS what the customer was quoted) instead of the
 * re-derived ex → inc figure, which can sit a cent off ($500 → $454.55 ex →
 * $500.01). Display only — the ex figure stored is the same either way.
 */
import { useMemo } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { getTenantTaxRate } from '@/utils/taxRate';
import { taxWordForRate } from '@shared/money/tax';
import { exFromIncPrice, incFromExPrice } from '@shared/money/billing-lines';
import { roundMoney } from '@shared/money/round';
import { formatCurrency } from '@/utils/pricing';

/** Two-decimal currency, the shape every pricing surface here wants. */
const CENTS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/**
 * Convert a typed inc-GST string to the ex-GST number to STORE.
 * Returns NaN for blank/unparseable input so callers can keep their existing
 * `Number.isNaN` validation shape.
 */
export function incStringToExGst(value: string, rate: number): number {
  const trimmed = value.trim();
  if (trimmed === '') return NaN;
  const inc = Number(trimmed);
  if (!Number.isFinite(inc)) return NaN;
  return exFromIncPrice(inc, rate);
}

/**
 * Convert a stored ex-GST number to the inc-GST string to SEED the input
 * with. `null`/`undefined` seeds an empty box rather than "0.00" — an unset
 * price and a zero price are different things in the price-history audit.
 */
export function exGstToIncString(
  exGst: number | null | undefined,
  rate: number,
): string {
  if (exGst == null || !Number.isFinite(exGst)) return '';
  return incFromExPrice(exGst, rate).toFixed(2);
}

/** Which basis the office types in: inc-GST (default) or ex-GST. Storage is ex-GST either way. */
export type PriceEntryMode = 'inc' | 'ex';

/**
 * A typed string in `mode` → the ex-GST number to STORE. `'inc'` is
 * `incStringToExGst`; `'ex'` stores the typed figure rounded to cents.
 * NaN for blank/unparseable input, like `incStringToExGst`.
 */
export function entryStringToExGst(value: string, rate: number, mode: PriceEntryMode = 'inc'): number {
  if (mode === 'inc') return incStringToExGst(value, rate);
  const trimmed = value.trim();
  if (trimmed === '') return NaN;
  const ex = Number(trimmed);
  if (!Number.isFinite(ex)) return NaN;
  return roundMoney(ex);
}

/** A stored ex-GST number → the string to SEED a box typed in `mode` (empty for null). */
export function exGstToEntryString(
  exGst: number | null | undefined,
  rate: number,
  mode: PriceEntryMode = 'inc',
): string {
  if (mode === 'inc') return exGstToIncString(exGst, rate);
  if (exGst == null || !Number.isFinite(exGst)) return '';
  return roundMoney(exGst).toFixed(2);
}

export interface IncGstPriceInputProps {
  /** Current value of the box, as an inc-GST string (controlled) — ex-GST when `entryMode` is `'ex'`. */
  value: string;
  onChange: (next: string) => void;
  /** Field label. The "(inc GST)" — or, in `'ex'` mode, "(ex GST)" — suffix is appended for you. */
  label?: string;
  /** Marks the label with a required asterisk. */
  required?: boolean;
  /** Appended after the amount in the ex-GST shadow line, e.g. "/mo". */
  unitSuffix?: string;
  /** Rendered to the right of the ex-GST shadow line (e.g. a "vs plan" delta). */
  trailing?: React.ReactNode;
  /** Extra helper text under the shadow line. */
  hint?: React.ReactNode;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  /** Override the tenant rate (tests, or a screen that already resolved it). */
  rate?: number;
  className?: string;
  /**
   * The basis the office types in (default `'inc'`). `'ex'`: the typed value
   * is the ex-GST figure stored as-is; the shadow line shows the inc figure.
   */
  entryMode?: PriceEntryMode;
  /** R2: in `'inc'` entry, "Customer pays" shows the typed figure (to cents), not the re-derived one. */
  customerPaysAsTyped?: boolean;
}

/**
 * Controlled inc-GST money input with the ex-GST figure shown underneath.
 *
 * ```tsx
 * const [priceInc, setPriceInc] = useState(exGstToIncString(service.monthlyPrice, rate));
 * <IncGstPriceInput value={priceInc} onChange={setPriceInc} label="New monthly price" unitSuffix="/mo" required />
 * // on save: updateServicePrice(id, incStringToExGst(priceInc, rate), ...)
 * ```
 */
export function IncGstPriceInput({
  value,
  onChange,
  label,
  required,
  unitSuffix = '',
  trailing,
  hint,
  placeholder = '0.00',
  autoFocus,
  disabled,
  id,
  rate: rateOverride,
  className = '',
  entryMode = 'inc',
  customerPaysAsTyped = false,
}: IncGstPriceInputProps) {
  const { tenant } = useTenant();
  const rate = rateOverride ?? getTenantTaxRate(tenant);
  const taxWord = taxWordForRate(rate);

  // Re-derive from the stored basis so the shadow line can never claim a
  // figure the rest of the app would render differently.
  const { exGst, incGst, hasValue } = useMemo(() => {
    const ex = entryStringToExGst(value, rate, entryMode);
    if (Number.isNaN(ex)) return { exGst: 0, incGst: 0, hasValue: false };
    const inc = customerPaysAsTyped && entryMode === 'inc' ? roundMoney(Number(value.trim())) : incFromExPrice(ex, rate);
    return { exGst: ex, incGst: inc, hasValue: true };
  }, [value, rate, entryMode, customerPaysAsTyped]);
  const exEntry = entryMode === 'ex';

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-theme-secondary mb-2"
        >
          {label} ({exEntry ? 'ex' : 'inc'} {taxWord}){required ? ' *' : ''}
        </label>
      )}
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none">
          $
        </span>
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          aria-label={
            exEntry
              ? label
                ? `${label} excluding ${taxWord}`
                : `Price excluding ${taxWord}`
              : label
                ? `${label} including ${taxWord}`
                : `Price including ${taxWord}`
          }
          className="w-full pl-8 pr-4 py-3 bg-surface-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 disabled:opacity-50"
        />
      </div>
      <div className="mt-1.5 flex items-start justify-between gap-2 text-xs">
        <span className="text-theme-muted">
          {hasValue ? (
            <>
              Customer pays{' '}
              <span className="text-brand-secondary font-medium">
                {formatCurrency(incGst, CENTS)}
                {unitSuffix}
              </span>{' '}
              · billed as{' '}
              <span className="text-theme-secondary font-medium">
                {formatCurrency(exGst, CENTS)}
                {unitSuffix}
              </span>{' '}
              ex {taxWord}
            </>
          ) : exEntry ? (
            <>Enter the price before {taxWord}.</>
          ) : (
            <>Enter the price the customer pays, {taxWord} included.</>
          )}
        </span>
        {trailing}
      </div>
      {hint && <div className="mt-1 text-[11px] text-theme-muted">{hint}</div>}
    </div>
  );
}

export default IncGstPriceInput;
